/**
 * DE (Decision Engine) Approval Amount enforcement for L4/L5 customers.
 * Compares the enforcement baseline (pre-override, pre-promo) Weekly Rate / Total Cash Price
 * against the DE-approved amounts, and resolves the SM/DM override tier from store config.
 */

import {
  DE_ENFORCEMENT_REASON,
  DE_APPROVAL_STATUS,
} from '../constants/constants';
import {
  DEOverrideRoles,
  DEEnforcementConfig,
  DEEnforcementInput,
  DEEnforcementEvaluation,
  DEEnforcementTagEventParams,
} from '../types/types';

// Falls back to the two currently-wired flows (Rental = 'RTO', Customer Order = 'SPEC')
// when the store hasn't configured EnforcedAgreementTypesForDE.
export const DEFAULT_ENFORCED_AGREEMENT_TYPES: string[] = ['RTO', 'SPEC'];

export const DEFAULT_DE_ENFORCEMENT_CONFIG: DEEnforcementConfig = {
  enabled: false,
  levelConfigs: {},
  enforcedAgreementTypes: DEFAULT_ENFORCED_AGREEMENT_TYPES,
};

// Exported so baseline-capture call sites (Rental/CO ItemSearch) can share this instead of
// `Number(value) || null`, which incorrectly treats a legitimate 0 as missing.
export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Parses non-JSON store-config values like "{SM:10,DM:20}" into { SM: 10, DM: 20 }. */
const parseRoles = (value: unknown): DEOverrideRoles => {
  if (typeof value !== 'string' || value.trim() === '') return {};

  try {
    const quoted = value.replace(
      /([{,]\s*)([A-Za-z][\w-]*)(\s*:)/g,
      '$1"$2"$3'
    );
    const parsed = JSON.parse(quoted);
    return Object.keys(parsed || {}).reduce((roles: DEOverrideRoles, role) => {
      const percentage = toFiniteNumber(parsed[role]);
      if (percentage !== null && percentage >= 0) roles[role] = percentage;
      return roles;
    }, {});
  } catch {
    return {};
  }
};

/** Parses a comma-separated store-config value like "RTO,SPEC" into an upper-cased array. */
const parseAgreementTypesList = (value: unknown): string[] => {
  if (typeof value !== 'string' || value.trim() === '') return [];
  return value
    .split(',')
    .map((type) => type.trim().toUpperCase())
    .filter((type) => type.length > 0);
};

/** Splits paramConfigs into the L1..L5 role levels. */
const buildLevelConfigs = (
  paramConfigs: any[]
): {
  levelConfigs: Record<string, DEOverrideRoles>;
  invalidLevels: string[];
} => {
  const invalidLevels: string[] = [];
  const levelConfigs = paramConfigs.reduce(
    (levels: Record<string, DEOverrideRoles>, levelConfig: any) => {
      const configName = String(levelConfig?.configName || '');
      const level = configName.replace(/^L/i, '');
      const roles = parseRoles(levelConfig?.configValue);
      if (level && Object.keys(roles).length > 0) levels[level] = roles;
      else if (level) invalidLevels.push(level);
      return levels;
    },
    {}
  );
  return { levelConfigs, invalidLevels };
};

// Store config identifies Customer Order as 'SPOR', but the real backend agreementType
// refCode on the agreement itself is 'SPEC' (see pendingAgreement.tsx) — alias it so the
// configured value still matches at evaluation time.
const AGREEMENT_TYPE_CONFIG_ALIASES: Record<string, string> = { SPOR: 'SPEC' };

// EnforcedAgreementTypesForDE is its own top-level paramKeyName (sibling to
// DEApprovalAmountOverride and EnableDEEnforcement), not nested under it.
// Matched on paramKeyName alone — paramGroupName varies per key in the real store config
// (e.g. EnableDEEnforcement lives under 'AgreementAccess', not 'DE_Restrictions').
const resolveEnforcedAgreementTypes = (configDetails: any[]): string[] => {
  const entry = configDetails.find(
    (config) => config?.paramKeyName === 'EnforcedAgreementTypesForDE'
  );
  if (!entry) return DEFAULT_ENFORCED_AGREEMENT_TYPES;
  return parseAgreementTypesList(entry.paramValue).map(
    (type) => AGREEMENT_TYPE_CONFIG_ALIASES[type] || type
  );
};

export const parseDEEnforcementConfig = (
  configDetails: any[] = []
): DEEnforcementConfig => {
  // Enablement is a dedicated feature-flag entry, separate from DEApprovalAmountOverride —
  // the latter only carries the L4/L5 override-percentage configs, not an enable/disable value.
  // Matched on paramKeyName alone — paramGroupName varies per key in the real store config
  // (EnableDEEnforcement lives under 'AgreementAccess', DEApprovalAmountOverride under 'DE_Restrictions').
  const enableEntry = configDetails.find(
    (config) => config?.paramKeyName === 'EnableDEEnforcement'
  );
  if (enableEntry?.paramValue !== '1') return DEFAULT_DE_ENFORCEMENT_CONFIG;

  const entry = configDetails.find(
    (config) => config?.paramKeyName === 'DEApprovalAmountOverride'
  );

  const paramConfigs = entry?.paramConfigs || [];
  const { levelConfigs, invalidLevels } = buildLevelConfigs(paramConfigs);
  const enforcedAgreementTypes = resolveEnforcedAgreementTypes(configDetails);

  return {
    enabled: true,
    levelConfigs,
    invalidLevels,
    configurationError: Object.keys(levelConfigs).length === 0,
    enforcedAgreementTypes,
  };
};

/** Raw (non-floored) exceedance percentage of actual over approved amount. */
const exceedance = (actual: unknown, approved: unknown): number => {
  const actualAmount = toFiniteNumber(actual);
  const approvedAmount = toFiniteNumber(approved);
  if (actualAmount === null || approvedAmount === null) return 0;
  if (approvedAmount <= 0)
    return actualAmount > 0 ? Number.MAX_SAFE_INTEGER : 0;
  return Math.max(0, ((actualAmount - approvedAmount) / approvedAmount) * 100);
};

/** Rounds an exceedance percentage to 2 decimals for display, e.g. 23.67. */
export const formatExceedPercent = (exceedPercent: number): string =>
  Number.isFinite(exceedPercent) ? exceedPercent.toFixed(2) : '0.00';

/** Entitlement checks: is DE enforcement even applicable to this agreement/customer. */
const resolveEntitlementBypassReason = (
  input: DEEnforcementInput,
  config: DEEnforcementConfig
): string | null => {
  if (!config.enabled) return DE_ENFORCEMENT_REASON.DISABLED_AT_STORE;
  if (input.isSwitchOut) return DE_ENFORCEMENT_REASON.SWITCHOUT;

  const agreementType = input.agreementType
    ? String(input.agreementType).toUpperCase()
    : undefined;
  const enforcedAgreementTypes =
    config.enforcedAgreementTypes ?? DEFAULT_ENFORCED_AGREEMENT_TYPES;
  if (agreementType && !enforcedAgreementTypes.includes(agreementType)) {
    return DE_ENFORCEMENT_REASON.AGREEMENT_TYPE_NOT_ENFORCED;
  }

  if (!input.deStatus) return DE_ENFORCEMENT_REASON.NO_DE;
  if (
    input.deStatus !== DE_APPROVAL_STATUS.APPROVED &&
    input.deStatus !== DE_APPROVAL_STATUS.CONDITIONAL_APPROVAL
  ) {
    return DE_ENFORCEMENT_REASON.DE_DECLINED;
  }
  return null;
};

/** Data-availability checks: is there enough valid config/amount data to compute a tier. */
const resolveDataAvailabilityBypassReason = (
  input: DEEnforcementInput,
  config: DEEnforcementConfig
): string | null => {
  if (
    config.configurationError ||
    config.invalidLevels?.includes(String(input.verifyLevel))
  ) {
    return DE_ENFORCEMENT_REASON.INVALID_CONFIGURATION;
  }
  if (!config.levelConfigs[String(input.verifyLevel)]) {
    return DE_ENFORCEMENT_REASON.LEVEL_NOT_ENFORCED;
  }
  if (
    toFiniteNumber(input.weeklyRate) === null ||
    toFiniteNumber(input.totalCashPrice) === null
  ) {
    return DE_ENFORCEMENT_REASON.ENFORCEMENT_AMOUNTS_UNAVAILABLE;
  }
  if (
    toFiniteNumber(input.weeklyApprovalAmount) === null ||
    toFiniteNumber(input.approvalAmount) === null
  ) {
    return DE_ENFORCEMENT_REASON.DE_APPROVAL_AMOUNTS_UNAVAILABLE;
  }
  return null;
};

/** All BYPASS checks prior to the exceed-percent/tier calculation. Returns null to continue. */
const getBypassEvaluation = (
  input: DEEnforcementInput,
  config: DEEnforcementConfig
): DEEnforcementEvaluation | null => {
  const reason =
    resolveEntitlementBypassReason(input, config) ??
    resolveDataAvailabilityBypassReason(input, config);
  return reason ? { status: 'BYPASS', reason, exceedPercent: 0 } : null;
};

/**
 * Shared by Rental and Customer Order's runDEEnforcementCheck(): always resolves DE
 * approval from the primary customer's GetApproval response — never the co-customer
 * flow's separate context values. If unavailable, evaluateDEEnforcement bypasses as NO_DE.
 */
export const resolveDEApprovalDetails = (params: {
  getCustomerResponse: any;
}) => {
  const resolvedApprovalDetails =
    params.getCustomerResponse?.GetApproval?.value?.customerApprovalDetails;
  return {
    deStatus: resolvedApprovalDetails?.approvalStatus,
    verifyLevel: resolvedApprovalDetails?.verifyLevel,
    approvalAmount: resolvedApprovalDetails?.approvalAmount,
    weeklyApprovalAmount: resolvedApprovalDetails?.weeklyApprovalAmount,
  };
};

export const evaluateDEEnforcement = (
  input: DEEnforcementInput
): DEEnforcementEvaluation => {
  const { config = DEFAULT_DE_ENFORCEMENT_CONFIG } = input;
  const bypass = getBypassEvaluation(input, config);
  if (bypass) return { ...bypass, verifyLevel: input.verifyLevel };

  const levelRoles = config.levelConfigs[String(input.verifyLevel)];
  const exceedPercent = Math.max(
    exceedance(input.weeklyRate, input.weeklyApprovalAmount),
    exceedance(input.totalCashPrice, input.approvalAmount)
  );
  if (exceedPercent <= 0)
    return { status: 'PASS', exceedPercent, verifyLevel: input.verifyLevel };

  // Honor whatever override roles the store has configured for this level —
  // no hardcoded role whitelist. parseRoles() is the sole gatekeeper of valid keys.
  const tier = Object.entries(levelRoles)
    .sort((left, right) => left[1] - right[1])
    .find(([, percentage]) => exceedPercent <= percentage)?.[0];

  if (!tier) {
    return {
      status: 'HARD_BLOCK',
      reason: DE_ENFORCEMENT_REASON.EXCEEDS_MAXIMUM_AUTHORITY,
      exceedPercent,
      verifyLevel: input.verifyLevel,
    };
  }

  return {
    status: 'APPROVAL_REQUIRED',
    exceedPercent,
    tier,
    moduleName: `DEOverride_${tier}`,
    verifyLevel: input.verifyLevel,
  };
};

// The "no-enforcement edge case" reasons (SPOS-16139 AC#4) that should emit a BYPASS tag
// event. Other bypass reasons (disabled at store, invalid config, level/agreement type not
// enforced, amounts unavailable) are operational, not business edge cases, and are
// intentionally excluded from tagging.
export const DE_BYPASS_TAG_REASONS: string[] = [
  DE_ENFORCEMENT_REASON.NO_DE,
  DE_ENFORCEMENT_REASON.DE_DECLINED,
  DE_ENFORCEMENT_REASON.SWITCHOUT,
  DE_ENFORCEMENT_REASON.REINSTATEMENT,
];

/**
 * Builds a GA dataLayer payload for the DE enforcement/override/bypass tag (SPOS-16139).
 * Reuses the app's existing window.dataLayer.push(...) GA integration (see the
 * "Loaded the X page" events elsewhere in AgreementDetails.tsx/Deliverymethod.tsx) —
 * this is the first *action* event added to that dataLayer rather than a page-load event.
 */
export const buildDEEnforcementTagEvent = (
  params: DEEnforcementTagEventParams
) => ({
  event: 'DE Enforcement Tag',
  tagType: params.tagType,
  bypassReason: params.bypassReason ?? null,
  overrideTier: params.overrideTier ?? null,
  overridePercentage: params.overridePercentage ?? null,
  exceedPercent:
    params.exceedPercent !== undefined && params.exceedPercent !== null
      ? formatExceedPercent(params.exceedPercent)
      : null,
  verifyLevel: params.verifyLevel ?? null,
  approverId: params.approverId ?? null,
  approverRole: params.approverRole ?? null,
  authenticationMethod: params.authenticationMethod ?? null,
  agreementNo: params.agreementNo ?? null,
  customerId: params.customerId ?? null,
  storeNo: params.storeNo ?? null,
  employeeId: params.employeeId ?? null,
  role: params.role ?? null,
  currentScreen: params.currentScreen,
});
